'use client'

import { EDIT_PARAM_KEY } from '../constants'
import { useSearchParams } from 'next/navigation'
import React, {
    createContext,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
    useCallback,
    useEffect,
} from 'react'

interface Props {
    /** Pass along a "restrictTo(ROLES)" to make sure the correct roles can actually enter the edit state */
    isAllowedToEdit: boolean
    children: ReactNode
}

interface ProviderValue {
    isEditing: boolean
    setIsEditing: Dispatch<SetStateAction<boolean>>
    isAllowedToEdit: boolean
}

export const EditStateContext = createContext<ProviderValue | undefined>(undefined)

/**
 * Updates the address bar via `replaceState` only when the target differs. `null` is passed
 * as the state so Next.js's patched `replaceState` runs its `copyNextJsInternalHistoryState`
 * path, which re-attaches `__NA` and `__PRIVATE_NEXTJS_INTERNALS_TREE` from the current
 * `history.state` and notifies `usePathname` / `useSearchParams`. Forwarding `history.state`
 * directly would short-circuit that patch (because it already carries `__NA: true`) and
 * prevent the App Router from picking up the URL change.
 */
const replaceLocationIfChanged = (nextPathSearchHash: string): void => {
    if (typeof window === 'undefined') return
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (nextPathSearchHash === current) return
    history.replaceState(null, '', nextPathSearchHash)
}

/**
 * Adds or removes the `editing` query param on the current URL while keeping pathname,
 * hash, and other query keys intact.
 */
const applyEditingToCurrentUrl = (newEditingState: boolean, isAllowedToEditFlag: boolean): void => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (isAllowedToEditFlag && newEditingState) {
        url.searchParams.set(EDIT_PARAM_KEY, 'true')
    } else {
        url.searchParams.delete(EDIT_PARAM_KEY)
    }
    const nextLocation = `${url.pathname}${url.search}${url.hash}`
    replaceLocationIfChanged(nextLocation)
}

/**
 * Edit mode is derived from the `editing` query param via Next.js `useSearchParams`, which
 * reactively reflects native `history.pushState` / `history.replaceState` updates in the
 * App Router. `setIsEditing` writes through `history.replaceState` so `history.state`
 * (needed by the App Router) and other query keys are preserved via `URL` / `URLSearchParams`.
 */
export const EditStateProvider = ({ isAllowedToEdit, children }: Props) => {
    const searchParams = useSearchParams()
    const isEditing = isAllowedToEdit && searchParams?.has(EDIT_PARAM_KEY) === true

    /**
     * If the user is not allowed to edit, delete the editing query param from the URL
     */
    useEffect(() => {
        if (isAllowedToEdit || typeof window === 'undefined') return
        const params = new URLSearchParams(window.location.search)
        if (!params.has(EDIT_PARAM_KEY)) return
        params.delete(EDIT_PARAM_KEY)
        const queryString = params.toString()
        const nextLocation = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`
        replaceLocationIfChanged(nextLocation)
    }, [isAllowedToEdit])

    const setIsEditing = useCallback<Dispatch<SetStateAction<boolean>>>(
        (update) => {
            const current =
                isAllowedToEdit &&
                typeof window !== 'undefined' &&
                new URLSearchParams(window.location.search).has(EDIT_PARAM_KEY)
            const resolved = typeof update === 'function' ? update(current) : update
            if (!isAllowedToEdit) {
                applyEditingToCurrentUrl(false, false)
                return
            }
            applyEditingToCurrentUrl(Boolean(resolved), true)
        },
        [isAllowedToEdit]
    )

    return (
        <EditStateContext.Provider value={{ isEditing, setIsEditing, isAllowedToEdit }}>
            {children}
        </EditStateContext.Provider>
    )
}
